import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import Login from './pages/Login';
import Home from './pages/Home';
import NewSale from './pages/NewSale';
import AddStock from './pages/AddStock';
import NewReturn from './pages/NewReturn';
import Products from './pages/Products';
import Customers from './pages/Customers';
import Reports from './pages/Reports';
import Dashboard from './pages/Dashboard';
import Settings from './pages/Settings';
import { CircleNotch } from '@phosphor-icons/react';

function ProtectedRoute({ children, permission, title }) {
  const { user, loading, hasPermission } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <CircleNotch size={40} className="animate-spin text-[#22C55E]" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (permission && !hasPermission(permission)) {
    return <Navigate to="/" replace />;
  }

  return <Layout title={title}>{children}</Layout>;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <CircleNotch size={40} className="animate-spin text-[#22C55E]" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />

      <Route path="/" element={<ProtectedRoute permission="dashboard"><Home /></ProtectedRoute>} />

      <Route path="/sales/new" element={<ProtectedRoute permission="sales" title="New Sale"><NewSale /></ProtectedRoute>} />

      <Route path="/inventory/add" element={<ProtectedRoute permission="inventory" title="Add Stock"><AddStock /></ProtectedRoute>} />
      {/* Redirect old routes to merged Add Stock page */}
      <Route path="/inventory/adjust" element={<Navigate to="/inventory/add?tab=adjust" replace />} />
      <Route path="/inventory/upload" element={<Navigate to="/inventory/add?tab=upload" replace />} />

      <Route path="/returns/new" element={<ProtectedRoute permission="returns" title="Process Return"><NewReturn /></ProtectedRoute>} />

      <Route path="/products" element={<ProtectedRoute permission="products" title="Products"><Products /></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute permission="customers" title="Customers"><Customers /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute permission="reports" title="Reports"><Reports /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute permission="reports" title="Dashboard"><Dashboard /></ProtectedRoute>} />
      {/* Redirect old Sales History to Reports */}
      <Route path="/sales" element={<Navigate to="/reports?tab=sales-history" replace />} />
      <Route path="/returns" element={<Navigate to="/reports?tab=returns" replace />} />
      <Route path="/settings" element={<ProtectedRoute permission="settings" title="Admin Settings"><Settings /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
